import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Star,
  StarOff,
  Building2,
  Upload,
  Download,
  ChevronLeft,
  ChevronRight,
  Filter,
  ExternalLink,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

export const UniversitiesManager = () => {
  const { token, hasPermission, canApprove, logout } = useCMSAuth();
  const navigate = useNavigate();
  
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [countryStats, setCountryStats] = useState([]);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canWrite = hasPermission('universities', 'write');
  const canDelete = hasPermission('universities', 'delete');

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  useEffect(() => {
    fetchUniversities();
    fetchCountryStats();
  }, [page, searchQuery, countryFilter, statusFilter]);

  const fetchUniversities = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '15'
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (countryFilter && countryFilter !== 'all') params.append('country', countryFilter);
      if (statusFilter === 'active') params.append('is_active', 'true');
      if (statusFilter === 'inactive') params.append('is_active', 'false');
      if (statusFilter === 'featured') params.append('is_featured', 'true');
      if (statusFilter === 'partner') params.append('is_partner', 'true');

      const res = await fetch(`${API_URL}/api/cms/universities?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (handleUnauthorized(res)) return;

      if (res.ok) {
        const data = await res.json();
        setUniversities(data.universities);
        setTotalPages(data.total_pages);
        setTotal(data.total);
      }
    } catch (e) {
      toast.error('Failed to fetch universities');
    } finally {
      setLoading(false);
    }
  };

  const fetchCountryStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/universities/stats/by-country`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleUnauthorized(res)) return;
      if (res.ok) {
        const data = await res.json();
        setCountryStats(data);
      }
    } catch (e) {
      console.error('Failed to fetch country stats');
    }
  };

  const handleToggleFeatured = async (university) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/universities/${university.id}/toggle-featured`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.draft_created) {
          toast.info('Change submitted for approval');
        } else {
          toast.success(data.message);
          fetchUniversities();
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update');
      }
    } catch (e) {
      toast.error('Failed to toggle featured status');
    }
  };

  const handleTogglePartner = async (university) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/universities/${university.id}/toggle-partner`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.draft_created) {
          toast.info('Change submitted for approval');
        } else {
          toast.success(data.message);
          fetchUniversities();
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update');
      }
    } catch (e) {
      toast.error('Failed to toggle partner status');
    }
  };

  const handleDelete = async () => {
    if (!selectedUniversity) return;
    setDeleting(true);

    try {
      const res = await fetch(`${API_URL}/api/cms/universities/${selectedUniversity.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.draft_id) {
          toast.info('Delete request submitted for approval');
        } else {
          toast.success('University deleted successfully');
          fetchUniversities();
        }
        setDeleteDialogOpen(false);
        setSelectedUniversity(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete');
      }
    } catch (e) {
      toast.error('Failed to delete university');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="universities-manager">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ux-charcoal">Universities</h1>
            <p className="text-ux-charcoal/60">
              Manage university listings ({total} total)
            </p>
          </div>
          
          <div className="flex gap-2">
            {canWrite && (
              <>
                <Button variant="outline" size="sm">
                  <Upload className="w-4 h-4 mr-1" /> Import CSV
                </Button>
                <Button 
                  size="sm" 
                  className="bg-ux-cyan hover:bg-ux-cyan-dark"
                  onClick={() => navigate('/admin/universities/new')}
                  data-testid="add-university-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add University
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ux-charcoal/50" />
                <Input
                  placeholder="Search universities..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>

              {/* Country Filter */}
              <Select value={countryFilter} onValueChange={(v) => { setCountryFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full md:w-48" data-testid="country-filter">
                  <Globe className="w-4 h-4 mr-2 text-ux-charcoal/50" />
                  <SelectValue placeholder="All Countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Countries</SelectItem>
                  {countryStats.map((c) => (
                    <SelectItem key={c.country} value={c.country}>
                      {c.country_name} ({c.total})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2 text-ux-charcoal/50" />
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="partner">Partners</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin w-8 h-8 border-4 border-ux-cyan border-t-transparent rounded-full" />
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Rank</TableHead>
                      <TableHead>University</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {universities.length > 0 ? (
                      universities.map((uni) => (
                        <TableRow key={uni.id} data-testid={`university-row-${uni.id}`}>
                          <TableCell className="font-medium">
                            {uni.qs_rank ? `#${uni.qs_rank}` : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                                {uni.logo_url ? (
                                  <img src={uni.logo_url} alt="" className="w-full h-full object-contain" />
                                ) : (
                                  <Building2 className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-ux-charcoal">{uni.name}</p>
                                <p className="text-xs text-ux-charcoal/60">{uni.city}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{uni.country_name}</TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              {!uni.is_active && (
                                <Badge variant="secondary" className="bg-gray-100 text-gray-600">Inactive</Badge>
                              )}
                              {uni.is_featured && (
                                <Badge className="bg-amber-100 text-amber-700">Featured</Badge>
                              )}
                              {uni.is_partner && (
                                <Badge className="bg-ux-cyan/10 text-ux-cyan">Partner</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              {canWrite && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleToggleFeatured(uni)}
                                    title={uni.is_featured ? 'Remove from featured' : 'Mark as featured'}
                                    data-testid={`toggle-featured-${uni.id}`}
                                  >
                                    {uni.is_featured ? (
                                      <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                                    ) : (
                                      <StarOff className="w-4 h-4 text-gray-400" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => navigate(`/admin/universities/${uni.id}/edit`)}
                                    data-testid={`edit-${uni.id}`}
                                  >
                                    <Edit className="w-4 h-4 text-ux-charcoal/60" />
                                  </Button>
                                </>
                              )}
                              {uni.website && (
                                <Button variant="ghost" size="icon" asChild>
                                  <a href={uni.website} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="w-4 h-4 text-ux-charcoal/60" />
                                  </a>
                                </Button>
                              )}
                              {(canDelete || canApprove()) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setSelectedUniversity(uni);
                                    setDeleteDialogOpen(true);
                                  }}
                                  data-testid={`delete-${uni.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-ux-charcoal/60">
                          No universities found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t">
                    <p className="text-sm text-ux-charcoal/60">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete University</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{selectedUniversity?.name}"? 
                {!canDelete && " This action requires approval from an administrator."}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default UniversitiesManager;
